import { useEffect, useRef, useState } from "react";
import { useColourPaletteStore } from "~/js/stores/colour-palette-store";
import { CircularInput, CircularThumb, CircularProgress } from "react-circular-input";

export function ColourWheel() {
  const [isDown, setIsDown] = useState(false);
  const rgbCode = useColourPaletteStore((state) => state.rgbCode);
  const setRgbCode = useColourPaletteStore((state) => state.setRgbCode);
  const [pointerColour, setPointerColour] = useState(rgbCode.split(',').map(color => parseInt(color)));
  const [pointerStyle, setPointerStyle] = useState({
    left: '50%',
    top: '50%',
    background: 'rgb(' + rgbCode + ')'
  });
  const [baseCtx, setBaseCtx] = useState(null);
  const [sliderValue, setSliderValue] = useState(0.25);
  const [colourShade, setColourShade] = useState(0.25);
  const [colourTone, setColourTone] = useState(rgbCode.split(',').map(color => parseInt(color)));
  const baseColor = useRef(null);
  const colourPng = useRef(null);
  const colourCanvas = useRef(null);
  const lastSliderChange = useRef(0);
  const sliderValueRef = useRef(sliderValue);
  const lastToneCall = useRef(0);

  useEffect(() => {
    sliderValueRef.current = sliderValue;
  }, [sliderValue]);

  useEffect(() => {
    const image = colourPng.current;
    const baseCanvas = colourCanvas.current;
    if (image && baseCanvas) {
      const ctx = baseCanvas.getContext('2d');
      image.onload = () => {
        ctx.drawImage(image, 1, 1, 280, 280);
        setBaseCtx(ctx);
      };
      // If image is already loaded (from cache)
      if (image.complete) {
        ctx.drawImage(image, 1, 1, 280, 280);
        setBaseCtx(ctx);
      }
    }
  }, []);

  useEffect(() => {
    getColour();
  }, [colourShade, colourTone]);

  useEffect(() => {
    const el = baseColor.current;
    if (!el) return;
    const handler = (e) => { e.preventDefault();};
    el.addEventListener("touchmove", handler, { passive: false });
  }, []);

  const getColour = () => {
    const shade = 200 * Math.abs(colourShade - 0.5); // convert to shade value from 0 to 100
    //If shade is 50, then the result colour is the same as the pointer colour.
    //If shade is 0, then the result colour is white.
    //If shade is 100, then the result colour is black.
    const colourValue = colourTone.map(colour => {
      if(shade > 50) {
        return Math.round(colour - ((shade - 50) * (colour / 50)));
      } else {
        return Math.round(colour + ((50 - shade) * ((255 - colour) / 50)));
      }
    });
    setRgbCode(colourValue[0] + ',' + colourValue[1] + ',' + colourValue[2]);
  }

  const getTone = () => {
    setColourTone(pointerColour);
  }

  const changePointer = (ev, isGettingTone) => {
    let rect = baseColor.current.getBoundingClientRect();
    let x; //x position within the element.
    let y;  //y position within the element.

    if (ev.type === "touchstart" || ev.type === "touchmove" || ev.type === "touchend") {
      x = ev.touches[0].clientX - rect.left;
      y = ev.touches[0].clientY - rect.top;
    } else {
      x = ev.clientX - rect.left;
      y = ev.clientY - rect.top;
    }

    // Check if the pointer is outside the circle with center (140, 140) and radius 135
    const dx = x - 140;
    const dy = y - 140;
    if (dx * dx + dy * dy > 135 * 135) {
      // the cursor is leaving the central circle
      if(isDown) getTone();
      setIsDown(false);
      // Clamp to the circle edge
      const angle = Math.atan2(dy, dx);
      x = 140 + 135 * Math.cos(angle);
      y = 140 + 135 * Math.sin(angle);
    }

    let p = baseCtx.getImageData(x, y, 1, 1).data;
    const colourRGB = p[0] + ',' + p[1] + ',' + p[2];

    setPointerStyle({
      left: x + 'px',
      top: y + 'px',
      background: 'rgb(' + colourRGB + ')'
    });

    setPointerColour([p[0], p[1], p[2]]);
    if(isGettingTone) { setColourTone([p[0], p[1], p[2]]);}
  }

  return (
    <div className="colour-wheel">
      <div className="colour-wheel__slider" style={{background: `rgb(${colourTone[0] + ',' + colourTone[1] + ',' + colourTone[2]})`}}>
        <div
          className="colour-wheel__round-slider"
          aria-hidden="true"
        >
          <CircularInput
            value={sliderValue}
            radius={175}
            onChange={(val) => {
              setSliderValue(val);
              const now = Date.now();
              if (now - lastSliderChange.current > 700) {
                setColourShade(val);
                lastSliderChange.current = now;
              }
            }}
            onChangeEnd={(val) => {
              setColourShade(val);
            }}
            clockwise
          >
            <CircularProgress stroke="transparent" strokeWidth={20}/>
            <CircularThumb
              className="colour-wheel__thumb"
              r={13}
              fill="#fff"
              stroke="#efefef"
              strokeWidth={4}
            />
          </CircularInput>
        </div>
      </div>
      <div
        ref={baseColor}
        className="colour-wheel__base-colour"
        onDragStart={(e) => e.preventDefault()}
        onClick={(e) => {
          changePointer(e, true);
        }}
        onMouseDown={() => {setIsDown(true);}}
        onMouseMove={(e) => {
          if (isDown) {
            changePointer(e, false);
            const now = Date.now();
            if (now - lastToneCall.current > 500) {
              getTone();
              lastToneCall.current = now;
            }
          }
        }}
        onMouseUp={(e) => {
          if (isDown) {
            setIsDown(false);
            getTone();
          }
        }}
        onMouseLeave={() => {
          if (isDown) {
            setIsDown(false);
            getTone();
          }
        }}
        onTouchStart={(e) => {
          changePointer(e, false);
          setIsDown(true);
        }}
        onTouchMove={(e) => {
          if (isDown) {
            changePointer(e, false);
            const now = Date.now();
            if (now - lastToneCall.current > 500) {
              getTone();
              lastToneCall.current = now;
            }
          }
        }}
        onTouchEnd={(e) => {
          if (isDown) {
            setIsDown(false);
            getTone();
          }
        }}
      >
      </div>

      <div className="colour-wheel__pointer-wrapper" >
        <div className="colour-wheel__pointer" style={pointerStyle}></div>
      </div>
      <div className="colour-wheel__hidden-canvas">
        <img src="/assets/img/colour-wheel-base-colours.png" alt="" ref={colourPng} width="280" height="280" />
        <canvas width="280" height="280" ref={colourCanvas}></canvas>
      </div>
    </div>
  );
}
